// ═══════════════════════════════════════════════════════════════════════════════
//  src/pages/EvaluacionPage.jsx
//  MICROSERVICIO ENGLISH ASSESSMENT — orquestador
//
//  Ruta: /evaluacion  (dentro de ProtectedRoute: presentar el examen es gratis
//  pero no anónimo — necesitamos saber de quién es la radiografía).
//
//  Máquina de estados:
//     loading → intro → running → result
//                 ↑                  │
//                 └──── reintentar ──┘
//
//  AISLAMIENTO: página autónoma con CSS prefijado `.as-`. Reutiliza el Header
//  de la app autenticada, como el resto de páginas de alumno, pero no modifica
//  ningún componente existente.
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Header from '../components/Header';
import usePageTracker from '../hooks/usePageTracker.js';

import AssessmentIntro from '../components/assessment/AssessmentIntro';
import AssessmentRunner from '../components/assessment/AssessmentRunner';
import AssessmentResult from '../components/assessment/AssessmentResult';
import { getStatus, startAssessment, getResult } from '../components/assessment/assessmentApi';

import '../components/assessment/assessment.css';

/** Nivel demostrado en el diagnóstico previo al registro, si existe. */
function readOnboardingHint() {
  try {
    const raw = localStorage.getItem('nsl_onboarding_v3');
    if (!raw) return {};
    const s = JSON.parse(raw);
    const cefr = s?.calibration_detail?.estimated_cefr;
    // El diagnóstico entrega bandas como "A2-B1": tomamos la inferior para no
    // arrancar por encima de lo que el alumno realmente demostró.
    const entryLevel = cefr ? String(cefr).split('-')[0] : null;
    const valid = ['A1', 'A2', 'B1', 'B2', 'C1'].includes(entryLevel) ? entryLevel : null;
    return { entryLevel: valid, onboardingSessionId: s?.session_id || null };
  } catch {
    return {};
  }
}

export default function EvaluacionPage() {
  usePageTracker('evaluacion', 'Evaluacion de nivel', null, 'studying');

  const navigate = useNavigate();

  const [phase, setPhase] = useState('loading');   // loading|intro|running|result|error
  const [status, setStatus] = useState(null);
  const [session, setSession] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  /* ── Al entrar: ¿ya lo hizo? ¿lo dejó a medias? ───────────────────────── */
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const st = await getStatus();
        if (!alive) return;
        setStatus(st);

        if (st.state === 'completed' && st.session_id) {
          const r = await getResult(st.session_id);
          if (!alive) return;
          setResult(r);
          setPhase('result');
          return;
        }
        setPhase('intro');
      } catch (e) {
        if (!alive) return;
        setError(e.message);
        setPhase('error');
      }
    })();
    return () => { alive = false; };
  }, []);

  /* ── Empezar o retomar ─────────────────────────────────────────────────── */
  const handleStart = useCallback(async () => {
    setPhase('loading');
    try {
      const data = await startAssessment(readOnboardingHint());

      if (data.finished) {
        const r = await getResult(data.session_id);
        setResult(r);
        setPhase('result');
        return;
      }

      setSession(data);
      setPhase('running');
    } catch (e) {
      setError(e.message);
      setPhase('error');
    }
  }, []);

  /* ── Al terminar el último reactivo ────────────────────────────────────── */
  const handleFinished = useCallback(async (payload) => {
    setPhase('loading');
    try {
      const r = await getResult(payload.session_id);
      setResult(r);
    } catch {
      // El resultado ya está calculado y guardado en el servidor. Si falla la
      // consulta, se muestra lo que trajo el último /answer en vez de perder
      // el cierre del examen.
      setResult({
        locked: false,
        overall_cefr: payload.overall_cefr,
        overall_score: payload.overall_score,
        skills: payload.skills || [],
      });
    }
    setPhase('result');
  }, []);

  /* ── Render ────────────────────────────────────────────────────────────── */
  const body = () => {
    if (phase === 'loading') {
      return (
        <div className="as-screen as-center-screen">
          <div className="as-spinner" aria-hidden="true" />
          <p className="as-hint">Preparando tu evaluación…</p>
        </div>
      );
    }

    if (phase === 'error') {
      return (
        <div className="as-screen">
          <h1 className="as-title">No pudimos cargar la evaluación</h1>
          <p className="as-lead">{error}</p>
          <div className="as-actions">
            <button type="button" className="as-btn" onClick={() => window.location.reload()}>
              Reintentar
            </button>
            <button type="button" className="as-link as-center" onClick={() => navigate('/entrenar')}>
              Volver al gimnasio
            </button>
          </div>
        </div>
      );
    }

    if (phase === 'intro') {
      return (
        <AssessmentIntro
          onStart={handleStart}
          resuming={status?.state === 'in_progress'}
          answered={status?.answered || 0}
          canViewResults={Boolean(status?.can_view_results)}
        />
      );
    }

    if (phase === 'running' && session) {
      return (
        <AssessmentRunner
          sessionId={session.session_id}
          firstItem={session.item}
          answeredSoFar={session.answered || 0}
          onFinished={handleFinished}
        />
      );
    }

    if (phase === 'result' && result) {
      return (
        <AssessmentResult
          result={result}
          onTrain={() => navigate('/entrenar')}
          /* Volver a medirse es EMPEZAR: se reutiliza el mismo camino que la
             primera vez. `POST /start` abre una sesión nueva cuando la
             anterior está terminada, así que no hace falta nada más. */
          onRepetir={handleStart}
        />
      );
    }

    return null;
  };

  return (
    <>
      <Header />
      <main className="as-root">
        {/* Ambiente. El resto de NSL las tiene y esta pantalla no: sin ellas el
            examen se veía como un documento pegado sobre la app en vez de una
            pantalla del producto. Son CSS puro —ni canvas ni JavaScript— porque
            aquí lo único que hacen falta son unos puntos que respiren.
            `aria-hidden` porque no comunican nada a quien no las ve. */}
        <div className="as-particulas" aria-hidden="true">
          {Array.from({ length: 14 }).map((_, i) => (
            <span key={i} className={`as-particula as-p${i % 7}`} />
          ))}
        </div>
        {body()}
      </main>
    </>
  );
}
